import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/api/auth'
import { ROUTES } from '@/utils/constants'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { ThemeSwitcher } from '@/components/common/ThemeSwitcher'
import { detachWebPushBeforeLogout } from '@/hooks/useWebPush'

interface SidebarFooterProps {
  onMenuClose?: () => void
}

export function SidebarFooter({ onMenuClose }: SidebarFooterProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)

  const getInitials = () => {
    const parts = [user?.surname, user?.first_name].filter(Boolean) as string[]
    if (parts.length) {
      return parts
        .join(' ')
        .split(' ')
        .filter(Boolean)
        .map((p) => p[0]?.toUpperCase())
        .join('')
        .slice(0, 2)
    }
    if (user?.email) return user.email[0]?.toUpperCase()
    return ''
  }

  const getUserDisplayName = () => {
    if (user?.name) return user.name
    if (user?.first_name) {
      const parts = [user.first_name, user.surname].filter(Boolean)
      if (parts.length > 0) return parts.join(' ')
    }
    if (user?.email) return user.email
    return t('dashboard.user')
  }

  const handleLogout = async () => {
    try {
      await detachWebPushBeforeLogout()
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      logout()
      navigate(ROUTES.LOGIN, { replace: true })
    }
  }

  const handleMenuItemClick = () => {
    onMenuClose?.()
    setIsProfileMenuOpen(false)
  }

  return (
    <div className="sidebar-footer">
      <div className="sidebar-language">
        <div className="sidebar-controls">
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
      </div>
      <div className={`sidebar-profile ${isProfileMenuOpen ? 'profile-menu-open' : ''}`}>
        <div className="user-profile-header">
          <button
            className="user-profile-toggle"
            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
          >
            <div className="user-avatar">
              {user?.avatar_url ? (
                <img className="user-avatar-img" src={user.avatar_url} alt={getUserDisplayName()} />
              ) : (
                getInitials() || <i className="fas fa-user"></i>
              )}
            </div>
            <span className="user-name">{getUserDisplayName()}</span>
            <i className={`fas fa-chevron-${isProfileMenuOpen ? 'up' : 'down'}`}></i>
          </button>
          <button
            className="user-logout-icon"
            onClick={handleLogout}
            title={t('auth.logout')}
          >
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
        {isProfileMenuOpen && (
          <div className="user-profile-menu">
            {user?.roles?.some((role) => role.slug === 'teacher') && (
              <Link
                to={ROUTES.ACCOUNTING}
                className={`nav-item ${location.pathname === ROUTES.ACCOUNTING ? 'active' : ''}`}
                onClick={handleMenuItemClick}
              >
                <i className="fas fa-calculator"></i>
                <span>{t('accounting.title')}</span>
              </Link>
            )}
            {user?.roles?.some((role) => role.slug === 'student') && (
              <>
                <Link
                  to={ROUTES.TEACHER}
                  className={`nav-item ${location.pathname === ROUTES.TEACHER ? 'active' : ''}`}
                  onClick={handleMenuItemClick}
                >
                  <i className="fas fa-chalkboard-teacher"></i>
                  <span>{t('teacherPage.title')}</span>
                </Link>
                <Link
                  to={ROUTES.FINANCE}
                  className={`nav-item ${location.pathname === ROUTES.FINANCE ? 'active' : ''}`}
                  onClick={handleMenuItemClick}
                >
                  <i className="fas fa-wallet"></i>
                  <span>{t('billing.studentFinanceTitle', 'Финансы')}</span>
                </Link>
              </>
            )}
            {user?.roles?.some((role) => role.slug === 'teacher' || role.slug === 'student') && (
              <Link
                to={ROUTES.VACATIONS}
                className={`nav-item ${location.pathname === ROUTES.VACATIONS ? 'active' : ''}`}
                onClick={handleMenuItemClick}
              >
                <i className="fas fa-umbrella-beach"></i>
                <span>{t('vacations.title', 'Отпуска')}</span>
              </Link>
            )}
            <Link
              to={ROUTES.PROFILE}
              className={`nav-item ${location.pathname === ROUTES.PROFILE ? 'active' : ''}`}
              onClick={handleMenuItemClick}
            >
              <i className="fas fa-user-circle"></i>
              <span>{t('dashboard.profile')}</span>
            </Link>
            <Link
              to={ROUTES.HELP}
              className={`nav-item ${location.pathname === ROUTES.HELP ? 'active' : ''}`}
              onClick={handleMenuItemClick}
            >
              <i className="fas fa-life-ring"></i>
              <span>{t('help.title')}</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
